import Link from 'next/link';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from 'tss-react/mui';
import WarningAmber from '@mui/icons-material/WarningAmber';
import SupervisedUserCircle from '@mui/icons-material/SupervisedUserCircle';
import Star from '@mui/icons-material/Star';
import LockIcon from '@mui/icons-material/Lock';
import { eventAgeRestrictions, eventTypes, visibilityLabels } from '../../utils/events';
import Tag from '../tag';
import { eventSeriesPath } from '../../utils/slugify';
import type { Event } from '../../types/Event';
import type { Group } from '../../types/Group';

const useStyles = makeStyles()((theme) => ({
  tags: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  featuredIcon: {
    color: theme.palette.secondary.main,
  },
  compactChipLabel: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    maxWidth: 150,
  },
  notPublic: {
    borderStyle: 'dashed',
  },
  seriesTag: {
    backgroundColor: theme.palette.action.hover,
    border: '1px solid rgba(0, 0, 0, 0.06)',
  },
}));

type Props = {
  event: Event;
  group?: Group;
  className?: string;
  compact?: boolean;
};

const EventHeaderTags = (props: Props) => {
  const { event, group, className, compact = false } = props;
  const { classes, cx } = useStyles();

  return (
    <div className={cx(classes.tags, className)}>
      {event.permissions?.accessible && !compact && event.eventType && (() => {
        const { name, icon: Icon } = eventTypes[event.eventType];
        return (
          <Tag variant="outlined">
            <Icon fontSize="small" />
            {name}
          </Tag>
        );
      })()}
      {event.status === 'CANCELLED' && (
        <Tag color="red">Cancelled</Tag>
      )}
      {event.status === 'DRAFT' && (
        <Tag color="orange">Draft</Tag>
      )}
      {event.status === 'GROUP_PENDING' && (
        <Tag color="red">
          Pending Approval
        </Tag>
      )}
      {event.visibility !== 'PUBLIC' && (
        <Tooltip title={event.visibility === 'GROUP' ? `Members of ${event.group?.name || 'group'}` : ''}>
          <Tag
            variant="outlined"
            className={classes.notPublic}
          >
            <LockIcon fontSize="small" />
            <span className={classes.compactChipLabel}>
              {event.visibility === 'GROUP' && event.group?.name
                ? event.group.name
                : visibilityLabels[event.visibility]}
            </span>
          </Tag>
        </Tooltip>
      )}
      {event.permissions?.accessible && (
        <>
          {event.audienceRestriction && (
            <Tag color="orange">
              <WarningAmber fontSize="small" />
              <span className={classes.compactChipLabel} title={event.audienceRestriction}>
                {event.audienceRestriction}
              </span>
            </Tag>
          )}
          {!compact && event.ageRestriction && event.ageRestriction !== 'GENERAL' && (
            <Tooltip title={eventAgeRestrictions[event.ageRestriction].description}>
              <Tag variant="outlined">
                <SupervisedUserCircle fontSize="small" />
                <span className={classes.compactChipLabel}>
                  {eventAgeRestrictions[event.ageRestriction].name}
                </span>
              </Tag>
            </Tooltip>
          )}
{event.series && (
            <Tooltip title={`${event.series.name} series`}>
              <Tag component={Link} href={eventSeriesPath(event.series.id, event.series.name)} className={classes.seriesTag}>Series</Tag>
            </Tooltip>
          )}
          {group && event.groupFeatured && (
            <Star className={classes.featuredIcon} />
          )}
        </>
      )}
    </div>
  );
};

export default EventHeaderTags;
